import React, { useState, useCallback, useRef, useEffect } from 'react';
import { showToast } from '../../utils/messageUtil';

/**
 * 日期时间范围选择器弹窗组件
 * - 从底部弹出，宽度接近全屏，高度约 70vh
 * - 包含日历视图 + 时分选择器 + 全天开关
 * - 支持范围选择（首次点开始，二次点结束，再次点击重置）
 */
export default function TimeRangePicker({
    open = false,
    onClose = () => {},
    initialStartTime = null,
    initialEndTime = null,
    isAllDay: initialAllDay = false,
    onConfirm = () => {},
    vehicleTimes = [],
}) {
    // ===== 初始化日期时间值 =====
    const initDate = initialStartTime instanceof Date && !isNaN(initialStartTime.getTime())
        ? initialStartTime
        : new Date();

    // 日历视图的年月
    const [viewYear, setViewYear] = useState(initDate.getFullYear());
    const [viewMonth, setViewMonth] = useState(initDate.getMonth()); // 0-based

    // 选择的日期范围
    // 不默认选中任何日期，由用户手动点击开始日期
    const getInitialRangeStart = () => {
        if (initialStartTime instanceof Date && !isNaN(initialStartTime.getTime())) {
            return new Date(initialStartTime.getFullYear(), initialStartTime.getMonth(), initialStartTime.getDate());
        }
        return null;
    };
    const [rangeStart, setRangeStart] = useState(getInitialRangeStart());
    const [rangeEnd, setRangeEnd] = useState(
        initialEndTime instanceof Date && !isNaN(initialEndTime.getTime())
            ? new Date(initialEndTime.getFullYear(), initialEndTime.getMonth(), initialEndTime.getDate())
            : null
    );

    /**
     * 当开始日期是今天时，校验输入的时分不能早于当前时分
     * 返回调整后的值（如果 < 当前时分则提升到当前时分）
     */
    const clampStartTime = (hour, minute) => {
        if (!isToday(rangeStart)) return { hour, minute };
        const now = new Date();
        const nowH = now.getHours();
        const nowM = now.getMinutes();
        if (hour < nowH) return { hour: nowH, minute: nowM };
        if (hour === nowH && minute < nowM) return { hour: nowH, minute: nowM };
        return { hour, minute };
    };

    /**
     * 从 initialStartTime/initialEndTime 获取初始时分值
     * 优先使用已选中的值，其次用当前时间（兜底）
     */
    const getInitialHour = (date) => {
        if (date instanceof Date && !isNaN(date.getTime())) return date.getHours();
        return new Date().getHours();
    };
    const getInitialMinute = (date) => {
        if (date instanceof Date && !isNaN(date.getTime())) return date.getMinutes();
        return new Date().getMinutes();
    };

    /**
     * 跟踪初始值 — picker 首次挂载时记录 initialStartTime/initialEndTime 的小时分钟
     * 即使 props 变化也保持不变，除非 picker 重新挂载（key 变化）
     */
    const initialStartRef = useRef(null);
    const initialEndRef = useRef(null);
    if (initialStartRef.current === null && initialStartTime instanceof Date && !isNaN(initialStartTime.getTime())) {
        initialStartRef.current = { hour: initialStartTime.getHours(), minute: initialStartTime.getMinutes() };
    }
    if (initialEndRef.current === null && initialEndTime instanceof Date && !isNaN(initialEndTime.getTime())) {
        initialEndRef.current = { hour: initialEndTime.getHours(), minute: initialEndTime.getMinutes() };
    }

    // 时间选择值 — 使用 initialStartTime/initialEndTime（页面上已有默认选中时间）
    // 用字符串存储，允许用户完全清空（空字符串），onConfirm 时校验非空
    const [startHour, setStartHour] = useState(() => String(getInitialHour(initialStartTime)));
    const [startMinute, setStartMinute] = useState(() => String(getInitialMinute(initialStartTime)));
    const [endHour, setEndHour] = useState(() => String(getInitialHour(initialEndTime)));
    const [endMinute, setEndMinute] = useState(() => String(getInitialMinute(initialEndTime)));

    const [allDay, setAllDay] = useState(initialAllDay);

    // 选择阶段: 'start' | 'end' | 'complete'
    const [selectionPhase, setSelectionPhase] = useState(
        rangeStart && rangeEnd ? 'complete' : rangeStart ? 'end' : 'start'
    );

    // 时间滚动容器 refs
    const startHourRef = useRef(null);
    const startMinRef = useRef(null);
    const endHourRef = useRef(null);
    const endMinRef = useRef(null);
    // 标记是否正在程序性滚动（避免 onScroll 死循环）
    const isScrollingStartHour = useRef(false);
    const isScrollingStartMin = useRef(false);
    const isScrollingEndHour = useRef(false);
    const isScrollingEndMin = useRef(false);

    // 当月天数
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    // 当月第一天是星期几 (0=周日, 6=周六)
    const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
    // 上个月天数
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    // 计算上个/下个月的年月
    const getPrevMonth = () => {
        if (viewMonth === 0) return { year: viewYear - 1, month: 11 };
        return { year: viewYear, month: viewMonth - 1 };
    };
    const getNextMonth = () => {
        if (viewMonth === 11) return { year: viewYear + 1, month: 0 };
        return { year: viewYear, month: viewMonth + 1 };
    };

    /** 生成6行×7列的日历网格数据 */
    const generateCalendarGrid = useCallback(() => {
        const grid = [];
        const prev = getPrevMonth();
        const totalCells = 42; // 6 rows × 7 columns

        for (let i = 0; i < totalCells; i++) {
            let day, month, year, isCurrentMonth;

            if (i < firstDayOfMonth) {
                // 上个月日期
                day = daysInPrevMonth - firstDayOfMonth + i + 1;
                month = prev.month;
                year = prev.year;
                isCurrentMonth = false;
            } else if (i >= firstDayOfMonth + daysInMonth) {
                // 下个月日期
                day = i - firstDayOfMonth - daysInMonth + 1;
                const next = getNextMonth();
                month = next.month;
                year = next.year;
                isCurrentMonth = false;
            } else {
                // 本月日期
                day = i - firstDayOfMonth + 1;
                month = viewMonth;
                year = viewYear;
                isCurrentMonth = true;
            }

            grid.push({ day, month, year, isCurrentMonth });
        }
        return grid;
    }, [viewYear, viewMonth, firstDayOfMonth, daysInMonth, daysInPrevMonth, getPrevMonth, getNextMonth]);

    /** 判断两个日期是否同一天 */
    const isSameDay = (d1, d2) => {
        if (!d1 || !d2) return false;
        return d1.getFullYear() === d2.getFullYear()
            && d1.getMonth() === d2.getMonth()
            && d1.getDate() === d2.getDate();
    };

    /** 判断日期是否在范围内（含边界） */
    const isInRange = (date) => {
        if (!rangeStart || !rangeEnd) return false;
        const ts = date.getTime();
        const startTs = rangeStart.getTime();
        const endTs = rangeEnd.getTime();
        const minTs = Math.min(startTs, endTs);
        const maxTs = Math.max(startTs, endTs);
        return ts > minTs && ts < maxTs;
    };

    /** 判断是否是今天 */
    const isToday = (date) => {
        const now = new Date();
        return isSameDay(date, now);
    };

    /** 判断是否是过去的日期（严格小于今天，不能选） */
    const isPastDate = (date) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return target.getTime() < today.getTime();
    };

    /**
     * 检查指定日期是否有车辆预约（全天模式使用）
     */
    const hasReservationOnDate = (date) => {
        const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return vehicleTimes.some(item => {
            const start = new Date(item.startTime);
            const end = new Date(item.endTime);
            const itemStartDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const itemEndDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            return checkDate >= itemStartDate && checkDate <= itemEndDate;
        });
    };

    /** 处理日期点击 */
    const handleDayClick = (cell) => {
        const clickedDate = new Date(cell.year, cell.month, cell.day);

        // 全天模式：单日选择，不进入 range 模式
        if (allDay) {
            if (hasReservationOnDate(clickedDate)) {
                showToast('所选日期当天有预约，无法选择全天', 'error');
                return;
            }
            setRangeStart(clickedDate);
            setRangeEnd(clickedDate);  // 同一天，让高亮显示为选中
            setSelectionPhase('complete');
            setStartHour(0);
            setStartMinute(0);
            setEndHour(23);
            setEndMinute(59);
            setViewYear(cell.year);
            setViewMonth(cell.month);
            return;
        }

        if (selectionPhase === 'start' || selectionPhase === 'complete') {
            // 首次点击或重置后点击 → 设置开始日期
            setRangeStart(clickedDate);
            setRangeEnd(null);
            setSelectionPhase('end');
            // 点击日期时：保留用户已选的时分，不再强制重置为 00:00 或当前时间
            // 这样 picker 始终显示初始/已选的开始结束时间
            const now = new Date();
            const isTodayClicked = clickedDate.getFullYear() === now.getFullYear()
                && clickedDate.getMonth() === now.getMonth()
                && clickedDate.getDate() === now.getDate();
            if (isTodayClicked && Number(startHour) === 0 && Number(startMinute) === 0) {
                // 仅当用户从未设置过时（仍为初始 0），且点击今天，才设为当前时间
                setStartHour(String(now.getHours()));
                setStartMinute(String(now.getMinutes()));
            }
            // 切换到点击的月份
            setViewYear(cell.year);
            setViewMonth(cell.month);
        } else if (selectionPhase === 'end') {
            // 第二次点击 → 设置结束日期
            let endDate = clickedDate;
            let startDate = rangeStart;

            // 如果结束日 < 开始日，自动交换
            if (endDate.getTime() < startDate.getTime()) {
                [startDate, endDate] = [endDate, startDate];
                // 时间也交换
                setStartHour(endHour);
                setStartMinute(endMinute);
                setEndHour(startHour);
                setEndMinute(startMinute);
            }

            setRangeStart(startDate);
            setRangeEnd(endDate);
            setSelectionPhase('complete');

            if (endDate.getTime() > startDate.getTime()) {
                // 跨天，结束时间默认 23:59
                setEndHour('23');
                setEndMinute('59');
            } else {
                // 同一天，确保结束时间 > 开始时间
                const sH = Number(startHour), sM = Number(startMinute);
                const eH = Number(endHour), eM = Number(endMinute);
                if (eH <= sH || (eH === sH && eM <= sM)) {
                    setEndHour(String(sH + 1));
                    setEndMinute(String(sM));
                }
            }
        }
    };

    /** 上个月 */
    const goToPrevMonth = () => {
        const prev = getPrevMonth();
        setViewYear(prev.year);
        setViewMonth(prev.month);
    };

    /** 下个月 */
    const goToNextMonth = () => {
        const next = getNextMonth();
        setViewYear(next.year);
        setViewMonth(next.month);
    };

    /** 确认选择 */
    const handleConfirm = () => {
        if (!rangeStart) return;

        // 校验时分非空（用户清空后必须重新填写）
        if (startHour === '' || startMinute === '' || endHour === '' || endMinute === '') {
            showToast('请填写完整的开始和结束时间');
            return;
        }

        let sH = Number(startHour), sM = Number(startMinute);
        const eH = Number(endHour), eM = Number(endMinute);

        let finalStart, finalEnd;
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const isAllDayToday = rangeStart.getTime() === today.getTime();

        // 今天是开始日期时：开始时分不能小于当前时分，小于则自动提升到当前时分
        if (rangeStart.getTime() === today.getTime()) {
            const nowH = now.getHours(), nowM = now.getMinutes();
            if (sH < nowH || (sH === nowH && sM < nowM)) {
                sH = nowH;
                sM = nowM;
                showToast('开始时间已调整为当前时间');
            }
        }

        if (allDay) {
            // 全天模式：
            // - 当天：从当前时间（startHour/startMinute）到 23:59
            // - 其他日期：00:00 到 23:59
            if (isAllDayToday) {
                finalStart = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate(), sH, sM, 0);
            } else {
                finalStart = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate(), 0, 0, 0);
            }
            finalEnd = rangeEnd
                ? new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate(), 23, 59, 59)
                : new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate(), 23, 59, 59);
        } else {
            finalStart = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate(), sH, sM, 0);
            if (rangeEnd) {
                finalEnd = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate(), eH, eM, 0);
            } else {
                finalEnd = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate(), eH, eM, 0);
            }

            // 确保结束 >= 开始
            if (finalEnd <= finalStart) {
                finalEnd = new Date(finalStart.getTime() + 3600000); // +1小时
            }
        }

        onConfirm(finalStart, finalEnd, allDay);
        onClose();
    };

    /** 取消 */
    const handleCancel = () => {
        onClose();
    };

    /** 全天开关：开启时检查当天是否有预约冲突 */
    const handleAllDayToggle = () => {
        if (allDay) {
            // 已经是全天 → 关闭，恢复范围选择模式
            setAllDay(false);
            setSelectionPhase('start');
            setRangeStart(null);
            setRangeEnd(null);
            return;
        }

        if (!rangeStart) {
            // 没有选中日期，只开启全天开关，让用户点击日期后再校验
            setAllDay(true);
            return;
        }

        // 有选中日期 → 检查该日期是否有预约
        if (hasReservationOnDate(rangeStart)) {
            showToast('所选日期当天有预约，无法选择全天', 'error');
            return;
        }

        // 没有预约 → 开启全天，自动设置 00:00 - 23:59
        setAllDay(true);
        setSelectionPhase('complete');
        setStartHour(0);
        setStartMinute(0);
        setEndHour(23);
        setEndMinute(59);
    };

    /** 每次打开弹窗时：保留 useState 的初始值（已用 initialStartTime/initialEndTime 初始化），
     * 不再强制重置为当前时间。删除这两个重置 effect，避免覆盖初始值。 */

    /** 首次打开弹窗时，滚动到当前时间（带重试，DOM 可能还没布局完） */
    useEffect(() => {
        if (!open) return;

        const scrollOne = (ref, value, isRef, itemHeight = 36) => {
            if (!ref.current) return;
            isRef.current = true;
            ref.current.scrollTop = (Number(value) + 1) * itemHeight;
            setTimeout(() => { isRef.current = false; }, 200);
        };

        const doScroll = () => {
            scrollOne(startHourRef, startHour, isScrollingStartHour);
            scrollOne(startMinRef, startMinute, isScrollingStartMin);
            scrollOne(endHourRef, endHour, isScrollingEndHour);
            scrollOne(endMinRef, endMinute, isScrollingEndMin);
        };

        // 首次挂载 DOM 布局可能未就绪，最多重试 5 次（500ms）
        let attempts = 0;
        const timer = setInterval(() => {
            attempts++;
            doScroll();
            if (attempts >= 5) clearInterval(timer);
        }, 100);
        requestAnimationFrame(() => doScroll());

        return () => clearInterval(timer);
    }, [open]);

    /** 用户交互（点日期/滚轮）导致 startHour/startMinute 变化时滚动（不重试） */
    useEffect(() => {
        if (!open) return;

        const scrollOne = (ref, value, isRef, itemHeight = 36) => {
            if (!ref.current) return;
            isRef.current = true;
            ref.current.scrollTop = (Number(value) + 1) * itemHeight;
            setTimeout(() => { isRef.current = false; }, 200);
        };

        requestAnimationFrame(() => {
            scrollOne(startHourRef, startHour, isScrollingStartHour);
            scrollOne(startMinRef, startMinute, isScrollingStartMin);
            scrollOne(endHourRef, endHour, isScrollingEndHour);
            scrollOne(endMinRef, endMinute, isScrollingEndMin);
        });
    }, [startHour, startMinute, endHour, endMinute]);

    /** G40：点击日期（含当天）时，主动滚动开始时分到对应位置 */
    useEffect(() => {
        if (!open || !rangeStart) return;
        // 异步执行，等 setStartHour 提交完
        const timer = setTimeout(() => {
            const now = new Date();
            const isToday = rangeStart.getFullYear() === now.getFullYear()
                && rangeStart.getMonth() === now.getMonth()
                && rangeStart.getDate() === now.getDate();
            // 如果是今天且已有已选开始时间，用已选时间；否则用当前时间
            const hasInitialStart = initialStartTime instanceof Date && !isNaN(initialStartTime.getTime());
            const isSameDayAsInitial = hasInitialStart
                && rangeStart.getFullYear() === initialStartTime.getFullYear()
                && rangeStart.getMonth() === initialStartTime.getMonth()
                && rangeStart.getDate() === initialStartTime.getDate();
            // ★ 与初始开始日期同一天（含未来日期默认 8:00 场景）：保持初始时分，
            //   不能因打开弹窗被重置为 0（此前仅今天走 initialStartTime，非今天直接落 0）
            let targetHour, targetMinute;
            if (isSameDayAsInitial) {
                targetHour = initialStartTime.getHours();
                targetMinute = initialStartTime.getMinutes();
            } else if (isToday) {
                targetHour = now.getHours();
                targetMinute = now.getMinutes();
            } else {
                targetHour = 0;
                targetMinute = 0;
            }
            // 同步更新 Start time state（不只用 scroll）
            setStartHour(targetHour);
            setStartMinute(targetMinute);
            if (startHourRef.current) {
                isScrollingStartHour.current = true;
                startHourRef.current.scrollTop = (targetHour + 1) * 32;
                setTimeout(() => { isScrollingStartHour.current = false; }, 200);
            }
            if (startMinRef.current) {
                isScrollingStartMin.current = true;
                startMinRef.current.scrollTop = (targetMinute + 1) * 32;
                setTimeout(() => { isScrollingStartMin.current = false; }, 200);
            }
        }, 30);
        return () => clearTimeout(timer);
    }, [rangeStart, open]);

    /** 处理时间滚动选择（考虑顶部 2 个 spacer 项的偏移） */
    const handleTimeScroll = (ref, setter, max, isScrollingRef, itemHeight = 36) => {
        let lastIdx = -1;
        return () => {
            if (!ref.current) return;
            // 如果是程序性滚动（useEffect 里调用的 scrollToItem），跳过 setter
            if (isScrollingRef?.current) return;
            const scrollTop = ref.current.scrollTop;
            // 列表顶部有 2 个 spacer 项（72px），所以需要减掉这个偏移
            const adjustedScrollTop = Math.max(0, scrollTop - 2 * itemHeight);
            const idx = Math.round(adjustedScrollTop / itemHeight);
            const clamped = Math.max(0, Math.min(idx, max - 1));
            // 防止循环触发：值未变不更新
            if (clamped !== lastIdx) {
                lastIdx = clamped;
                setter(clamped);
            }
        };
    };

    if (!open) return null;

    const grid = generateCalendarGrid();
    const weekDays = ['日', '一', '二', '三', '四', '五', '六'];

    // 生成小时和分钟选项
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 60 }, (_, i) => i);

    const timeItemHeight = 32;

    // 格式化日期显示
    const formatDateShort = (date) => {
        if (!date) return '--';
        return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const formatTimeShort = (h, m) => {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    return (
        <div className="time-range-picker-overlay" onClick={handleCancel}>
            <div
                className="time-range-picker"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ===== 顶部导航 ===== */}
                <div className="time-range-picker-header">
                    <button className="trp-header-btn trp-header-btn-cancel" onClick={handleCancel}>
                        取消
                    </button>
                    <span className="trp-header-title">请选择日期</span>
                    <label className="trp-all-day-toggle">
                        <span className="trp-all-day-label">全天</span>
                        <div
                            className={`trp-toggle-switch ${allDay ? 'active' : ''}`}
                            onClick={handleAllDayToggle}
                        >
                            <div className="trp-toggle-knob" />
                        </div>
                    </label>
                </div>

                {/* ===== 日历区域 ===== */}
                <div className="time-range-picker-calendar">
                    {/* 月份切换 */}
                    <div className="trp-month-nav">
                        <button className="trp-month-btn" onClick={goToPrevMonth}>◀</button>
                        <span className="trp-month-title">{viewYear}年{viewMonth + 1}月</span>
                        <button className="trp-month-btn" onClick={goToNextMonth}>▶</button>
                    </div>

                    {/* 周标题 */}
                    <div className="trp-week-header">
                        {weekDays.map((day, i) => (
                            <div key={i} className="trp-week-day">{day}</div>
                        ))}
                    </div>

                    {/* 日期网格 */}
                    <div className="trp-day-grid">
                        {grid.map((cell, index) => {
                            const cellDate = new Date(cell.year, cell.month, cell.day);
                            const isRangeStartDay = rangeStart && isSameDay(cellDate, rangeStart);
                            const isRangeEndDay = rangeEnd && isSameDay(cellDate, rangeEnd);
                            const inRange = isInRange(cellDate);
                            const today = isToday(cellDate);
                            // 是否是过去的日期（仅对当前月判断；上/下月日期禁用点击跳转）
                            const isPast = isPastDate(cellDate);

                            let dayClass = 'trp-day';
                            if (!cell.isCurrentMonth) dayClass += ' trp-day-other-month';
                            if (isRangeStartDay) dayClass += ' trp-day-range-start';
                            if (isRangeEndDay) dayClass += ' trp-day-range-end';
                            if (isRangeStartDay && isRangeEndDay) {
                                dayClass += ' trp-day-range-single';
                            } else if (inRange) {
                                dayClass += ' trp-day-in-range';
                            }
                            if (today) dayClass += ' trp-day-today';
                            if (isPast) dayClass += ' trp-day-past';

                            return (
                                <div
                                    key={index}
                                    className={dayClass}
                                    onClick={() => !isPast && handleDayClick(cell)}
                                >
                                    <span className="trp-day-text">{cell.day}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ===== 时间选择器 ===== */}
                <div className={`time-range-picker-time ${allDay ? 'trp-time-disabled' : ''}`}>
                    {/* 开始时间列 */}
                    <div className="trp-time-column">
                        <div className="trp-time-column-title">开始时间</div>
                        <div className="trp-time-input-area">
                            <div className="trp-time-sub-column">
                                <div className="trp-time-sub-label">时</div>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="trp-time-input"
                                    maxLength={2}
                                    value={startHour}
                                    onChange={(e) => {
                                        setStartHour(e.target.value.replace(/\D/g, ''));
                                    }}
                                    onBlur={(e) => {
                                        // 空时保持空（不自动补 0），点击确定时校验
                                        const raw = e.target.value.replace(/\D/g, '');
                                        if (raw === '') return;
                                        let v = parseInt(raw, 10);
                                        v = Math.max(0, Math.min(23, v));
                                        setStartHour(String(v));
                                    }}
                                />
                            </div>
                            <span className="trp-time-colon">:</span>
                            <div className="trp-time-sub-column">
                                <div className="trp-time-sub-label">分</div>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="trp-time-input"
                                    maxLength={2}
                                    value={startMinute}
                                    onChange={(e) => {
                                        setStartMinute(e.target.value.replace(/\D/g, ''));
                                    }}
                                    onBlur={(e) => {
                                        const raw = e.target.value.replace(/\D/g, '');
                                        if (raw === '') return;
                                        let v = parseInt(raw, 10);
                                        v = Math.max(0, Math.min(59, v));
                                        setStartMinute(String(v));
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 分隔符 */}
                    <div className="trp-time-divider">—</div>

                    {/* 结束时间列 */}
                    <div className="trp-time-column">
                        <div className="trp-time-column-title">结束时间</div>
                        <div className="trp-time-input-area">
                            <div className="trp-time-sub-column">
                                <div className="trp-time-sub-label">时</div>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="trp-time-input"
                                    maxLength={2}
                                    value={endHour}
                                    onChange={(e) => {
                                        setEndHour(e.target.value.replace(/\D/g, ''));
                                    }}
                                    onBlur={(e) => {
                                        const raw = e.target.value.replace(/\D/g, '');
                                        if (raw === '') return;
                                        let v = parseInt(raw, 10);
                                        v = Math.max(0, Math.min(23, v));
                                        setEndHour(String(v));
                                    }}
                                />
                            </div>
                            <span className="trp-time-colon">:</span>
                            <div className="trp-time-sub-column">
                                <div className="trp-time-sub-label">分</div>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="trp-time-input"
                                    maxLength={2}
                                    value={endMinute}
                                    onChange={(e) => {
                                        setEndMinute(e.target.value.replace(/\D/g, ''));
                                    }}
                                    onBlur={(e) => {
                                        const raw = e.target.value.replace(/\D/g, '');
                                        if (raw === '') return;
                                        let v = parseInt(raw, 10);
                                        v = Math.max(0, Math.min(59, v));
                                        setEndMinute(String(v));
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="time-selection-divider">
                    如需跨天预约,依次点击2个日期,蓝色:开始,黄色:结束,红色:同一天
                </div>
                {/* ===== 底部摘要 + 按钮 ===== */}
                <div className="time-range-picker-footer">
                    <div className="trp-footer-summary">
                        <div className="trp-summary-row">
                            <span className="trp-summary-label">开始: </span>
                            <span className="trp-summary-value">
                                {rangeStart ? (
                                    allDay
                                        ? `${formatDateShort(rangeStart)} 00:00`
                                        : `${formatDateShort(rangeStart)} ${formatTimeShort(startHour, startMinute)}`
                                ) : '--'}
                            </span>
                        </div>
                        <div className="trp-summary-row">
                            <span className="trp-summary-label">结束: </span>
                            <span className="trp-summary-value">
                                {rangeEnd ? (
                                    allDay
                                        ? `${formatDateShort(rangeEnd)} 23:59`
                                        : `${formatDateShort(rangeEnd)} ${formatTimeShort(endHour, endMinute)}`
                                ) : rangeStart ? (
                                    allDay
                                        ? `${formatDateShort(rangeStart)} 23:59`
                                        : `${formatDateShort(rangeStart)} ${formatTimeShort(endHour, endMinute)}`
                                ) : '--'}
                            </span>
                        </div>
                    </div>
                    <div className="trp-footer-buttons">
                        <button className="trp-footer-btn trp-footer-btn-cancel" onClick={handleCancel}>
                            取消
                        </button>
                        <button
                            className="trp-footer-btn trp-footer-btn-confirm"
                            onClick={handleConfirm}
                            disabled={!rangeStart}
                        >
                            确定
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
